import { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";

const WHATSAPP_NUMBER = "918917209551";
const STORAGE_KEY = "auraa_wa_modal_seen";
const DELAY_MS = 4500; // shows ~4.5s after landing

export default function WhatsAppModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", property: "", interest: "" });

  // Auto-open once per session
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
    } catch (e) {
      /* ignore */
    }
    const t = window.setTimeout(() => setOpen(true), DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const composeMessage = () => {
    const lines = ["Hello Auraa Designs,"];
    if (form.name) lines.push(`I'm ${form.name}.`);
    if (form.property) lines.push(`I'm with ${form.property}.`);
    if (form.interest) lines.push(`We're looking at: ${form.interest}.`);
    if (lines.length === 1) {
      lines.push("I'd like to learn more about your hospitality uniform programmes.");
    } else {
      lines.push("I'd like to start a conversation about a hospitality uniform programme.");
    }
    return lines.join("\n\n");
  };

  const openWhatsApp = (e) => {
    e.preventDefault();
    const msg = encodeURIComponent(composeMessage());
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
    close();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  return (
    <div
      data-testid="whatsapp-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Chat with Auraa on WhatsApp"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        data-testid="whatsapp-modal-backdrop"
        className="absolute inset-0 bg-espresso/55 backdrop-blur-sm animate-fade-in"
      />

      {/* Modal panel */}
      <div
        data-testid="whatsapp-modal-panel"
        className="relative w-full max-w-lg bg-bone border border-borderwarm shadow-[0_20px_60px_-20px_rgba(44,30,22,0.45)] animate-fade-up"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 md:px-9 py-5 border-b border-borderwarm">
          <div className="flex items-center gap-3">
            <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366]">
              <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40" />
              <svg viewBox="0 0 32 32" className="relative w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                <path d="M19.11 17.16c-.27-.14-1.6-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.17-1.33-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.41.12-.55.13-.13.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.46-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.11 2.81.14.18 1.92 2.94 4.66 4.12.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.32zM16.02 5.33c-5.9 0-10.7 4.79-10.7 10.7 0 1.88.49 3.71 1.43 5.33L5 27l5.79-1.71c1.56.86 3.32 1.31 5.22 1.31h.01c5.9 0 10.7-4.79 10.7-10.7s-4.81-10.57-10.7-10.57z" />
              </svg>
            </span>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.32em] text-espresso-soft">WhatsApp</div>
              <div className="font-serif text-[18px] text-espresso">Talk to Auraa directly</div>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            data-testid="whatsapp-modal-close"
            className="p-1.5 -mr-1.5 text-espresso-soft hover:text-oxblood transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={openWhatsApp} className="px-7 md:px-9 py-7 md:py-8">
          <p className="text-[14px] md:text-[15px] leading-[1.7] text-espresso-soft font-light">
            A few quick lines and we'll open a WhatsApp chat with Archana —
            prefilled with your context so we can respond meaningfully.
          </p>

          <div className="mt-6 space-y-5">
            <Field label="Your Name">
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                data-testid="wa-modal-name"
                className="luxury-input"
                placeholder="e.g. Priya Menon"
              />
            </Field>
            <Field label="Property / Company">
              <input
                type="text"
                value={form.property}
                onChange={update("property")}
                data-testid="wa-modal-property"
                className="luxury-input"
                placeholder="e.g. The Sablon, Udaipur"
              />
            </Field>
            <Field label="What are you exploring?">
              <input
                type="text"
                value={form.interest}
                onChange={update("interest")}
                data-testid="wa-modal-interest"
                className="luxury-input"
                placeholder="New opening · Rebrand · Replenishment · Restaurant launch"
              />
            </Field>
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
            <button
              type="button"
              onClick={close}
              data-testid="wa-modal-later"
              className="text-[11px] uppercase tracking-[0.28em] text-espresso-soft hover:text-oxblood transition-colors duration-500 self-start sm:self-center"
            >
              Maybe later
            </button>
            <button
              type="submit"
              data-testid="wa-modal-submit"
              className="group inline-flex items-center justify-center gap-3 bg-[#1E3B2E] text-bone px-7 py-3.5 text-[12px] tracking-[0.18em] uppercase font-medium transition-all duration-500 hover:bg-[#25503C]"
            >
              Start WhatsApp Chat
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </div>
        </form>

        {/* Footer note */}
        <div className="px-7 md:px-9 py-4 border-t border-borderwarm bg-bone-200/40">
          <div className="text-[10px] uppercase tracking-[0.28em] text-espresso-soft">
            Opens WhatsApp · No fields required
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.28em] text-espresso font-medium mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
